import { useCallback, useEffect, useRef, useState } from 'react'
import { contact } from '../data/site'
import { ANALYTICS_EVENTS, pushAnalyticsEvent, trackWhatsAppClick } from '../lib/analytics'
import { WHATSAPP_WIDGET_OPEN_EVENT, WHATSAPP_WIDGET_PANEL_ID } from '../lib/whatsapp'

const defaultMessage = 'Hola CRS Cimentaciones. Quisiera información sobre un servicio.'

function WhatsAppIcon() {
  return (
    <svg className="whatsapp-brand-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.21-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479s1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.414-.074-.123-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.981.999-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.99c-.002 5.45-4.437 9.884-9.886 9.889m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.88 11.88 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
    </svg>
  )
}

export default function WhatsAppWidget() {
  const [isOpen, setIsOpen] = useState(false)
  const [message, setMessage] = useState(defaultMessage)
  const [status, setStatus] = useState('')
  const isOpenRef = useRef(false)
  const textareaRef = useRef(null)

  const closeWidget = useCallback(() => {
    if (!isOpenRef.current) return
    isOpenRef.current = false
    setIsOpen(false)
    pushAnalyticsEvent(ANALYTICS_EVENTS.WHATSAPP_WIDGET_CLOSE, { click_location: 'floating_widget' })
  }, [])

  const openWidget = useCallback(({ clickLocation = 'floating_widget', source = 'launcher' } = {}) => {
    if (!isOpenRef.current) {
      pushAnalyticsEvent(ANALYTICS_EVENTS.WHATSAPP_WIDGET_OPEN, {
        click_location: clickLocation,
        interaction_source: source,
      })
    }
    isOpenRef.current = true
    setIsOpen(true)
  }, [])

  useEffect(() => {
    if (isOpen) textareaRef.current?.focus()
  }, [isOpen])

  useEffect(() => {
    const handleEscape = (event) => { if (event.key === 'Escape') closeWidget() }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [closeWidget])

  useEffect(() => {
    const handleOpenRequest = (event) => openWidget(event.detail)
    document.addEventListener(WHATSAPP_WIDGET_OPEN_EVENT, handleOpenRequest)
    return () => document.removeEventListener(WHATSAPP_WIDGET_OPEN_EVENT, handleOpenRequest)
  }, [openWidget])

  const handleSubmit = (event) => {
    event.preventDefault()
    const preparedMessage = message.trim() || defaultMessage
    trackWhatsAppClick({
      clickLocation: 'whatsapp_widget',
      linkText: 'Enviar por WhatsApp',
      messageLength: preparedMessage.length,
      source: 'floating_widget',
    })
    setStatus('Abrimos WhatsApp con el mensaje preparado. Revísalo antes de enviarlo.')
    window.open(`${contact.whatsapp}?text=${encodeURIComponent(preparedMessage)}`, '_blank', 'noopener,noreferrer')
  }

  return (
    <aside className={`whatsapp-widget ${isOpen ? 'is-open' : ''}`} aria-label="Contacto por WhatsApp">
      {isOpen && (
        <div className="whatsapp-panel" id={WHATSAPP_WIDGET_PANEL_ID} role="dialog" aria-label="Enviar mensaje por WhatsApp">
          <header>
            <div className="whatsapp-panel-mark"><WhatsAppIcon /></div>
            <div><span>CRS Cimentaciones</span><small>Normalmente respondemos en horario laboral</small></div>
            <button type="button" onClick={closeWidget} aria-label="Cerrar WhatsApp" data-analytics-manual="true">×</button>
          </header>
          <div className="whatsapp-message"><span>Hola 👋</span><p>¿Podemos ayudarte con alguna cotización o duda?</p></div>
          <form onSubmit={handleSubmit}>
            <label htmlFor={`${WHATSAPP_WIDGET_PANEL_ID}-message`}>Tu mensaje</label>
            <textarea ref={textareaRef} id={`${WHATSAPP_WIDGET_PANEL_ID}-message`} value={message} onChange={(event) => setMessage(event.target.value)} rows="3" maxLength="600" />
            <button type="submit" data-analytics-manual="true"><WhatsAppIcon /><span>Enviar por WhatsApp</span></button>
            {status && <p className="whatsapp-status" role="status">{status}</p>}
          </form>
        </div>
      )}
      <button className="whatsapp-launcher" type="button" onClick={isOpen ? closeWidget : () => openWidget()} aria-expanded={isOpen} aria-controls={WHATSAPP_WIDGET_PANEL_ID} aria-label={isOpen ? 'Cerrar WhatsApp' : 'Abrir WhatsApp'} data-analytics-manual="true">
        <WhatsAppIcon />
        <span>{isOpen ? 'Cerrar' : '¿Necesitas ayuda?'}</span>
      </button>
    </aside>
  )
}
